import { useCallback, useEffect, useState } from "react";
import { ApiError, getApi, ZOD_PARSE_ERROR } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_PROBATION_QUESTIONS } from "../../../../../config/Endpoints";
import { z } from "zod";
import { ProbationQuestion, ProbationQuestionSchema, ProbationQuestionRequest } from "../schemas/index.ts";

export function useProbationQuestions({ cycleId, sectionType }: ProbationQuestionRequest) {
    const [questions, setQuestions] = useState<ProbationQuestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchQuestions = useCallback(async (signal?: AbortSignal) => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await getApi<ProbationQuestion[]>(`${API_PROBATION_QUESTIONS}?cycleId=${cycleId}&sectionType=${sectionType}`, accessToken(), "json", signal);

            if (!data) {
                setQuestions([]);
            } else {
                const parse = z.array(ProbationQuestionSchema).safeParse(data);

                if (!parse.success) {
                    setQuestions([]);
                    console.error("Parse error: ", parse.error);
                    setError(ZOD_PARSE_ERROR);
                } else {
                    setQuestions(parse.data);
                }
            }
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : "Failed to load probation questions.";
            setError(msg);
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    }, [cycleId, sectionType]);

    useEffect(() => {
        const ac = new AbortController();
        void fetchQuestions(ac.signal);

        return () => ac.abort();
    }, [fetchQuestions])

    return { questions, loading, error, refetch: fetchQuestions };
}

